<template>
  <!-- <h2 class="pb-half-margin-bottom">
    {{ translate('pb2.requestView.documentation') }}
  </h2> -->

  <SanitizedHtml
    :content="translate('pb2.requestView.documentationMessage')"
    class="pb-margin-bottom"
  />

  <UiDropzone
    v-model="files"
    :disabled="filePending"
    :file-types="types"
    :max-file-size="16 * MB"
    :locale="userStore.language"
    class="pb-margin-bottom"
  />

  <!-- <h2 class="pb-half-margin-bottom">
    {{ translate('pb2.requestView.requestDetails') }}
  </h2> -->
  <UiField
    class="pb-margin-bottom pb-request-view-support-message-field"
    :label="translate('pb2.requestView.supportMessage.label')"
  >
    <BaseInput
      v-model="message"
      class="pb-profile-request-form-textarea"
      :type="INPUT_TYPE_TEXTAREA"
      :placeholder="translate(repair ? 'pb2.requestView.supportMessage.repairPlaceholder' : 'pb2.requestView.supportMessage.createPlaceholder')"
    />
  </UiField>

  <UiField
    :label="translate(repair ? 'pb2.requestView.prioritySelect.repairLabel' : 'pb2.requestView.prioritySelect.createLabel')"
    :loading="prioritiesLoading"
  >
    <AutoSpinner class="pb-profile-request-form-priority-select-spinner">
      <BaseSelect
        v-model="priority"
        :options="priorities"
        :aria-label="translate('generic.pleaseSelect')"
        alignment="right"
        class="pb-profile-request-form-priority-select"
      >
        <template
          #toggler="{
            id,
            label,
            ariaLabel,
            disabled,
            state,
            onClick: open,
            onEscape: close,
            onUp: openOrPrev,
            onDown: openOrNext,
            onLeft: prev,
            onRight: next,
            selection,
          }"
        >
          <SelectDropdownToggler
            :id="id"
            key="toggler"
            :aria-label="ariaLabel"
            :disabled="disabled"
            :state="state"
            @click="open"
            @escape="close"
            @up="openOrPrev"
            @down="openOrNext"
            @left="prev"
            @right="next"
          >
            <div class="pb-profile-request-form-priority-select-toggler">
              <template v-if="selection">
                <div>
                  {{ selection.label || label }}
                </div>
                <div class="txt-muted txt-w-regular">
                  {{ selection.price }}
                </div>
              </template>
              <div
                v-else
                class="pb-profile-request-form-priority-select-placeholder"
              >
                {{ label }}
              </div>
            </div>
            <UiIcon
              icon="arrowdown"
              size="small"
              class="ui-select-dropdown-toggler-icon"
            />
          </SelectDropdownToggler>
        </template>

        <template #default="option">
          <div class="flex-grow txt-right">
            <div class="pb-profile-request-form-priority-select-title">
              {{ option.label }}
            </div>
            <div class="pb-profile-request-form-priority-select-price">
              {{ option.price }}
            </div>
          </div>
        </template>
      </BaseSelect>
    </AutoSpinner>
  </UiField>

  <MessageBox
    v-if="selectedPriorityInfo && !selectedPriorityInfo.free"
    color="info"
    class="pb-half-margin-top"
  >
    <p class="pb-margin-bottom">
      {{ translate('pb2.requestView.orderPaymentMessage') }}
    </p>
    <p>{{ translate('pb2.requestView.orderTaxesMessage') }}</p>
  </MessageBox>

  <FlexBox direction="row">
    <ControlButton
      :to="routerStore.prevRoute || { name: routeNames.Home }"
      :disabled="prioritiesLoading || submitLoading"
      :tooltip="translate('pb2.requestView.backButton.tooltip')"
      tooltip-location="bottom"
      class="pb-create-profile-link"
      :aria-label="translate('pb2.requestView.backButton.tooltip')"
      :label="translate('pb2.requestView.backButton.label')"
    />
    <ButtonWithLoader
      :is-loading="submitLoading"
      class="btn-success"
      tooltip-location="bottom"
      :tooltip="translate('pb2.requestView.sendButton.tooltip')"
      :aria-label="translate('pb2.requestView.sendButton.tooltip')"
      :label="translate('pb2.requestView.sendButton.label')"
      :disabled="!canSubmit"
      @click="submit"
    />
  </FlexBox>
</template>

<script setup>
import {
  computed,
  ref,
  shallowRef,
  watch,
} from 'vue'

import BaseSelect from '@nicolaudie-group/ng-ui-kit/components/Select/BaseSelect.vue'
import { INPUT_TYPE_TEXTAREA } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'
import UiDropzone, { MB } from '@nicolaudie-group/ng-ui-kit/components/UiDropzone/UiDropzone.vue'
import useFileReader from '@nicolaudie-group/ng-ui-kit/composables/useFileReader'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import FlexBox from '@nicolaudie-group/ng-ui-kit/components/FlexBox/FlexBox.vue'
import UiField from '@nicolaudie-group/ng-ui-kit/components/UiField/UiField.vue'
import BaseInput from '@nicolaudie-group/ng-ui-kit/components/BaseInput/BaseInput.vue'
import AutoSpinner from '@nicolaudie-group/ng-ui-kit/components/AutoSpinner/AutoSpinner.vue'
import SelectDropdownToggler from '@nicolaudie-group/ng-ui-kit/components/Select/SelectDropdownToggler.vue'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import SanitizedHtml from '@nicolaudie-group/ng-ui-kit/components/SanitizedHtml/SanitizedHtml.vue'
import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'
import useAction from '@/composables/useAction'
import useTranslate from '@/composables/useTranslate'
import { isPending } from '@nicolaudie-group/ng-ui-kit/helpers/isStatus'
import useAlerter from '@nicolaudie-group/ng-ui-kit/composables/useAlerter'
import MessageBox from '@nicolaudie-group/ng-ui-kit/components/MessageBox/MessageBox.vue'
import * as sdk from '@/helpers/sdk'
import PbError from '@/classes/PbError.class'
import useRouterStore from '@/stores/router.store'
import useUserStore from '@/stores/user.store'
import routeNames from '@/router/names'
import { buildOrderLink } from '@/helpers/profileRequests'
import { useBrands, useProfile, useProfileStatus } from '@/composables/useProfile'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import useProfileStore from '@/stores/profile.store'

// import { useProfile } from '@/composables/useProfile'

const routerStore = useRouterStore()
const userStore = useUserStore()
const profileStore = useProfileStore()

const profileStatus = useProfileStatus()
const profile = useProfile()
const brands = useBrands()

function validateBrand(brand) {
  if (brands.value.includes(brand)) return brand
  return '_varied'
}

const profileUid = computed(() => profileStore.profileUid || null)
const brand = computed(() => (
  profile.value?.brand || validateBrand(routerStore.route?.query?.brand)
))
const name = computed(() => (
  profile.value?.name || routerStore.route?.query?.name
))

const { translate } = useTranslate()

// const profile = useProfile()

const priority = ref(undefined)

const {
  files,
  idle: fileIdling,
  pending: filePending,
  success: fileSuccess,
  process,
} = useFileReader({
  auto: false,
})

const { success } = useAlerter()

const {
  // data: result,
  status: submitStatus,
  action: submit,
} = useAction(async () => {
  const readResult = await process()
  const file = readResult[0]?.value
  const fileContent = file?.content?.base64
  const fileName = file?.fullName
  // const fileContent = readResult[0]?.value?.content?.raw

  const payload = {
    profileUid: profileUid.value,
    brand: brand.value,
    name: name.value,
    priority: priority.value,
    document: fileContent,
    documentName: fileName,
    message: message.value,
  }

  let result
  try {
    result = await sdk.v3.profileRequests.create({
      body: payload,
    })

    // if (!loading.value) {
    // loading.value = true

    // if (creating.value) {
    //   processingCreate.value = true
    //   // If creating a new profile (not repair), create with the provided brand & name
    //   // const result = await profileStore.create(payload)
    //   // uid = result.uid
    // } else {

    //   // If there already is a profile (repair)
    // }

    // Redirect to profile properties once done
    // await routerStore.push({
    //   name: routeNames.Properties,
    //   params: {
    //     profileUid: uid,
    //   },
    // })
    dev.log(`Request made for ${payload.name} from ${payload.brand} (UID: ${payload.profileUid})`)

    if (!result || !result.data) {
      throw result
    }
  } catch (e) {
    const message = e?.error?.message
    throw new PbError({
      title: translate('pb2.requestView.sendError.title'),
      message: message ?? translate('pb2.requestView.sendError.message'),
      status: e?.status,
      errors: e?.errors,
    }, { cause: e })
  }

  success({
    title: translate('pb2.requestView.sendSuccess.title'),
    text: translate('pb2.requestView.sendSuccess.message'),
  })

  const { SRQ_ORDER } = result.data
  if (SRQ_ORDER) {
    window.open(buildOrderLink(SRQ_ORDER), '_blank')
  }

  await routerStore.push({ name: routeNames.Home })

  // processingCreate.value = false
  // loading.value = false
  // }
  return result
})

const repair = computed(() => !!profileUid.value)

const {
  data: priorities,
  status: prioritiesStatus,
  action: getPriorities,
} = useAction(async () => {
  if (brand.value) {
    const response = await sdk.v3.profileRequests.getPriorities({
      url: { brand: brand.value },
      query: { repair: repair.value },
    })
    if (Array.isArray(response.data)) {
      return response.data.map((option) => ({
        label: option.name,
        value: option.urgencyId,
        price: option.priceText,
        free: option.free,
      }))
    }
  }
  return []
}, { shallow: true, data: [] })

const prioritiesLoading = computed(() => isPending(prioritiesStatus.value))
const submitLoading = computed(() => isPending(submitStatus.value))

watch(priorities, (newPriorities) => {
  if (!newPriorities.length) {
    // If no priority has been fetched, clear the selected priority
    priority.value = undefined
  } else if (typeof priority.value === 'undefined') {
    // If the priority has not been already set, find and select the "free" priority by default
    priority.value = newPriorities.find((item) => item.free === true)?.value
  }
})

const selectedPriorityInfo = computed(() => (
  priorities.value.find((priorityInfo) => priorityInfo.value === priority.value)
))

watch(() => [brand.value, repair.value], ([newBrand]) => {
  if (newBrand) {
    getPriorities()
  }
}, { immediate: true })

const message = shallowRef('')

const canSubmit = computed(() => (
  // No profile is loading, or it loaded successfully
  (profileStatus.idle.value || profileStatus.success.value)
  // A file has been selected
  && files.value.length
  // File is valid and ready to (re-)upload
  && (fileIdling.value || fileSuccess.value)
  // A submit is not already in progress
  && !(submitLoading.value)
  // A message for support has been set
  && message.value
  // A priority has been set
  && priority.value
))

</script>

<script>

const types = [
  {
    mimeType: 'application/pdf',
    ext: '.pdf',
    typeName: 'pdf',
    title: 'PDF document',
  },
  {
    mimeType: 'image/jpeg',
    ext: '.jpg',
    typeName: 'jpg',
    title: 'JPEG image',
  },
  {
    mimeType: 'image/jpeg',
    ext: '.jpeg',
    typeName: 'jpg',
    title: 'JPEG image',
  },
  {
    mimeType: 'image/png',
    ext: '.png',
    typeName: 'png',
    title: 'PNG image',
  },
  {
    mimeType: 'application/msword',
    ext: '.doc',
    typeName: 'doc',
    title: 'Microsoft Word document',
  },
  {
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ext: '.docx',
    typeName: 'docx',
    title: 'Microsoft Word document',
  },
  {
    mimeType: 'application/vnd.ms-excel',
    ext: '.xls',
    typeName: 'xls',
    title: 'Microsoft Excel document',
  },
  {
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ext: '.xlsx',
    typeName: 'xlsx',
    title: 'Microsoft Excel document',
  },
  {
    mimeType: 'text/plain',
    ext: '.txt',
    typeName: 'txt',
    title: 'Text file',
  },
]
</script>

<style lang="scss">
@use '@nicolaudie-group/ng-ui-kit/defines/_fonts.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_colors.define.scss';
@use '@nicolaudie-group/ng-ui-kit/defines/_config.define.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_flexbox.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_generic.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_responsive.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_theme.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_animations.mixin.scss';
@use '@nicolaudie-group/ng-ui-kit/mixins/_transitions.mixin.scss';

@use '/src/styles/_pb.scss';

@use '@nicolaudie-group/ng-ui-kit/components/Listbox/listbox.scss';

.pb-profile-request-form-textarea {
  min-height: 6em;

  ::placeholder {
    font-style: italic;
  }
}

.pb-profile-request-form-priority-select-price {
  color: colors.$color-default--gray-average;
}

.pb-profile-request-form-priority-select-spinner {
  // Resize the spinner and it's margin to make it the same height as
  // the select, to avoid layout shift when the spinner disappears.
  min-height: 2.4em;
  margin: 0 !important;
}

.pb-profile-request-form-priority-select {
  // @include listbox.item-label {
  //   color:
  // }

  .pb-profile-request-form-priority-select-toggler {
    min-height: 2.4em;
  }

  .pb-profile-request-form-priority-select-placeholder {
    line-height: 2.4em;
  }

  .pb-profile-request-form-priority-select-title {
    font-weight: bold;
  }

  @include listbox.item-label-checked {
    .pb-profile-request-form-priority-select-title {
      color: colors.$color-default--blue;
    }

    .pb-profile-request-form-priority-select-price {
      font-weight: initial;
    }
  }

  @include listbox.item-label-hover {
    .pb-profile-request-form-priority-select-title {
      color: inherit;
    }

    .pb-profile-request-form-priority-select-price {
      color: inherit;
    }
  }
}
</style>
